/* ─────────────────────────────────────────────────────────
 *  SimStyleSpectrum
 *
 *  Web-Audio AnalyserNode  →  canvas spectrum analyzer
 *  that matches the visual style of the music-simulator's
 *  drawSpectrumAnalyzer (music_generator_canvas.ts).
 * ────────────────────────────────────────────────────── */

import React, { useEffect, useRef } from 'react';
import { drawSpectrumFrame } from './spectrumFrame';

export interface SimStyleSpectrumProps {
  analyser: AnalyserNode | null;
  status?: 'idle' | 'loading' | 'playing' | 'paused';
  fps?: number;
  color?: string;      // spectrum line / fill colour
  maxFreqHz?: number;  // X-axis max (default 20 000 Hz)
  className?: string;
}

const SimStyleSpectrum: React.FC<SimStyleSpectrumProps> = ({
  analyser,
  status = 'idle',
  fps = 60,
  color = '#9fffd8',
  maxFreqHz = 20000,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef    = useRef<HTMLCanvasElement | null>(null);
  const rafRef       = useRef<number>(0);
  const lastTsRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let running = true;
    const interval = 1000 / fps;

    const render = (ts: number) => {
      if (!running) return;

      // DPR-aware resize
      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const dpr  = window.devicePixelRatio || 1;
        const cw   = Math.round(rect.width);
        const ch   = Math.round(rect.height);
        if (canvas.width !== cw * dpr || canvas.height !== ch * dpr) {
          canvas.width  = cw * dpr;
          canvas.height = ch * dpr;
          canvas.style.width  = `${cw}px`;
          canvas.style.height = `${ch}px`;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
      }

      if (ts - lastTsRef.current >= interval) {
        lastTsRef.current = ts;
        const w = canvas.width  / (window.devicePixelRatio || 1);
        const h = canvas.height / (window.devicePixelRatio || 1);

        const nyquist = analyser
          ? (analyser.context?.sampleRate ?? 48000) / 2
          : 24000;

        let data: Uint8Array | null = null;
        if (analyser && status !== 'idle') {
          data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data as any);
        }

        drawSpectrumFrame(ctx, w, h, data, nyquist, maxFreqHz, color, status);
      }

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [analyser, fps, color, maxFreqHz, status]);

  return (
    <div ref={containerRef} className={`w-full h-full${className ? ` ${className}` : ''}`}
         style={{ minHeight: 80 }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </div>
  );
};

export default SimStyleSpectrum;
